import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "wouter";
import { TimelineShell } from "@/components/ui/TimelineShell";
import { appShell } from "@/store";
import {
  codecTimelineEntriesAtom,
  codecTimelineErrorAtom,
  codecTimelineFiltersAtom,
  codecTimelineHasMoreAtom,
  codecTimelineLoadingAtom,
  codecTimelineRequestKeyAtom,
  codecTimelineSelectionReadyAtom,
  fetchCodecTimelineAtom,
  licConsolidatedDataAtom,
  licDevicesAtom,
} from "@/store/data";
import { CodecEventTimelineCard } from "./components/CodecEventTimelineCard";
import { CodecMeshEventTimelineCard } from "./components/CodecMeshEventTimelineCard";
import { CodecTimelineFiltersBar } from "./components/CodecTimelineFiltersBar";
import { LastCommunicationCard } from "./components/LastCommunicationCard";
import { MessageRequestTimelineCard } from "./components/MessageRequestTimelineCard";
import { readCodecTimelineUrlParams } from "./model";

/**
 * CODEC Timeline report page: pick a CODEC and a date range, then inspect the
 * LIC-level device events for that window, newest first with day separators.
 * The view is deep-linkable via `codecId`, `start` and `end` URL parameters.
 */
function CodecTimelinePage() {
  const setBackButton = useSetAtom(appShell.backButtonAtom);
  const setFilters = useSetAtom(codecTimelineFiltersAtom);
  const fetchCodecTimeline = useSetAtom(fetchCodecTimelineAtom);
  const selectionReady = useAtomValue(codecTimelineSelectionReadyAtom);
  const requestKey = useAtomValue(codecTimelineRequestKeyAtom);
  const loading = useAtomValue(codecTimelineLoadingAtom);
  const entries = useAtomValue(codecTimelineEntriesAtom);
  const timelineError = useAtomValue(codecTimelineErrorAtom);
  const hasMore = useAtomValue(codecTimelineHasMoreAtom);
  const filters = useAtomValue(codecTimelineFiltersAtom);
  const licConsolidated = useAtomValue(licConsolidatedDataAtom);
  const licDevices = useAtomValue(licDevicesAtom);

  const selectedCodec = filters.codecId ? licDevices.find((device) => device.id === filters.codecId) : null;
  const latestPacketDate = filters.codecId
    ? (licConsolidated[filters.codecId]?.latestPacket?.packet_date ?? null)
    : null;
  const [searchParams] = useSearchParams();
  const lastAppliedParams = useRef<string | null>(null);
  const searchParamsKey = searchParams.toString();

  useEffect(() => {
    if (searchParamsKey === lastAppliedParams.current) return;
    lastAppliedParams.current = searchParamsKey;

    const next = readCodecTimelineUrlParams(new URLSearchParams(searchParamsKey));
    setFilters((prev) => {
      const codecId = next.codecId === undefined ? prev.codecId : next.codecId;
      const startDate = next.startDate ?? prev.startDate;
      const endDate = next.endDate ?? prev.endDate;
      const changed = codecId !== prev.codecId || startDate !== prev.startDate || endDate !== prev.endDate;
      if (!changed) {
        return prev;
      }
      return { codecId, startDate, endDate };
    });
  }, [searchParamsKey, setFilters]);

  useEffect(() => {
    setBackButton("/app/reports");
    return () => {
      setBackButton(false);
    };
  }, [setBackButton]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: requestKey intentionally triggers a refetch when filters/pagination change
  useEffect(() => {
    if (!selectionReady) return;
    fetchCodecTimeline();
  }, [fetchCodecTimeline, requestKey, selectionReady]);

  const handleRetry = useCallback(() => {
    if (!selectionReady) return;
    fetchCodecTimeline();
  }, [fetchCodecTimeline, selectionReady]);

  const handleLoadMore = useCallback(() => {
    if (loading) return;
    fetchCodecTimeline();
  }, [fetchCodecTimeline, loading]);

  return (
    <div className="min-h-full space-y-6 bg-gray-50 p-6 pb-20">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">Linha do Tempo do CODEC</h1>
        <p className="text-gray-600">
          Verifique o que o CODEC reportou em um período: eventos do dispositivo, chuva, tensão e mudanças de
          agendamento.
        </p>
      </header>

      <section className="rounded-xl border border-neutral-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Filtros</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Selecione o CODEC e ajuste o intervalo de datas para inspecionar a linha do tempo.
        </p>
        <div className="mt-4">
          <CodecTimelineFiltersBar />
        </div>
      </section>

      <LastCommunicationCard deviceIdentifier={selectedCodec?.identifier ?? null} latestPacketDate={latestPacketDate} />

      <section className="space-y-4 rounded-xl border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Eventos do CODEC</h2>
            <p className="text-sm text-gray-500">Eventos são exibidos em ordem decrescente de início.</p>
          </div>
        </div>
        <TimelineShell
          entries={entries}
          getTimestamp={(entry) => entry.startTime}
          getEntryKey={(entry) => entry.key}
          renderEntry={(entry) =>
            entry.source === "meshEvent" ? (
              <CodecMeshEventTimelineCard entry={entry} />
            ) : entry.source === "messageRequest" ? (
              <MessageRequestTimelineCard entry={entry} />
            ) : (
              <CodecEventTimelineCard entry={entry} />
            )
          }
          loading={loading}
          error={timelineError}
          notReady={!selectionReady}
          notReadyMessage="Selecione um CODEC para carregar a linha do tempo de eventos."
          emptyMessage="Nenhum evento encontrado para este CODEC no intervalo selecionado."
          onRetry={handleRetry}
        />
        {hasMore ? (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loading}
              className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                loading
                  ? "cursor-not-allowed border border-neutral-300 bg-neutral-100 text-neutral-500"
                  : "border border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {loading ? "Carregando..." : "Carregar mais"}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default CodecTimelinePage;
